import {
    AfterViewChecked,
    ChangeDetectorRef,
    Component,
    OnInit,
    Signal,
    computed,
    inject,
    DestroyRef,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { DividerComponent, GroupingLogicComplexComponent, InstructionsComponent } from '@r1-compass-library/components';
import {
    DataMartField,
    DataMartTable,
    DivisionConfig,
    SelectionCriterionType,
    TemplateCriteria,
    TemplateCriteriaConfig,
} from '@r1-compass-library/models';
import { DivisionCriteriaSourceSettings } from '@r1-compass-library/models/division/division-criteria-source-settings.model';
import { DataMartService } from '@r1-compass-library/services';
import { AssetStateMessageHandlingService } from '@r1-compass-library/services/asset-state-message-handling.service';
import { BehaviorSubject, combineLatest, take } from 'rxjs';
import { DataMartTablesStore } from '../store/data-mart-tables.store';
import {
    SelectionCriterion,
    SelectionCriterionData,
} from '@r1-compass-library/models/criteria/selection-criteria.model';
import { TemplateCriteriaStore } from '../store/template-criteria.store';
import { filterNil } from 'ngxtension/filter-nil';
import {
    GenericSelectionCriterionForm,
    SelectionCriterionForm,
    convertSelectionCriterionForm,
    SalesHistorySelectionCriterionDataForm,
} from '@r1-compass-library/models/criteria/selection-criteria-form.model';
import { MatButtonModule } from '@angular/material/button';
import { WebSearchCriterionComponent } from './criteria/web-search-criterion/web-search-criterion.component';
import { WebActivityCriterionComponent } from './criteria/web-activity-criterion/web-activity-criterion.component';
import { SalesHistoryCriterionComponent } from './criteria/sales-history-criterion/sales-history-criterion.component';
import { NarrowWideFormTypesPipe } from '@r1-compass-library/pipes/narrow-wide-form-types.pipe';
import { ActivatedRoute } from '@angular/router';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { GenericSelectionCriterionComponent } from './criteria/generic-selection-criterion/generic-selection-criterion.component';
import { MatMenuModule } from '@angular/material/menu';
import { TemplateCriteriaBuilderForm } from '@r1-compass-library/models/template-criteria/template-criteria-form.model';
import { DataMartModelsStore } from '@r1-compass-library/store/data-mart-models.store';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { SaveGuardStore } from '@r1-compass-library/guards/save-guard.store';
import { TemplateCriteriaBuilderSaveGuardWarning } from '@r1-compass-library/guards/save-guards/template-criteria-builder.guard';
import { BuilderSaveGuardWarning } from '@r1-compass-library/guards/save-guards/builder-warning.interface';
import { TemplateCriteriaFormMap, TemplateCriteriaValidationService } from '../template-criteria-validation.service';
import { TemplateCriteriaValidationStateService } from '../template-criteria-validation-state.service';
import {
    SyncValidationStateSection,
    SyncValidationStateService,
    ValidationService,
    ValidationStateService,
    TemplateCriteriaSectionName,
} from '@r1-compass-library/features/validation-sync';
import { AssetInvalidSaveWarningService } from '../../asset-invalid-save-warning.service';
import { noopFormSaveMutationResult } from '@r1-compass-library/guards/hasUnsavedChanges.guard';

@Component({
    selector: 'r1-compass-app-template-criteria-builder',
    imports: [
        DividerComponent,
        GenericSelectionCriterionComponent,
        GroupingLogicComplexComponent,
        InstructionsComponent,
        SalesHistoryCriterionComponent,
        WebActivityCriterionComponent,
        WebSearchCriterionComponent,
        MatButtonModule,
        MatFormFieldModule,
        MatSelectModule,
        MatMenuModule,
        ReactiveFormsModule,
        NarrowWideFormTypesPipe,
    ],
    templateUrl: './template-criteria-builder.component.html',
    styleUrl: './template-criteria-builder.component.scss',
})
export class TemplateCriteriaBuilderComponent
    implements
        OnInit,
        AfterViewChecked,
        TemplateCriteriaBuilderSaveGuardWarning,
        BuilderSaveGuardWarning,
        SyncValidationStateSection<TemplateCriteriaSectionName, TemplateCriteria>
{
    readonly #route = inject(ActivatedRoute);
    readonly #selectedTemplateStore = inject(TemplateCriteriaStore);
    readonly #tableStore = inject(DataMartTablesStore);
    readonly #dataMartService = inject(DataMartService);
    readonly #dataModelStore = inject(DataMartModelsStore);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #destroyRef$ = inject(DestroyRef);
    readonly #assetStateMessageHandlingService = inject(AssetStateMessageHandlingService);
    readonly #changeDetectorRef = inject(ChangeDetectorRef);
    readonly #formSaveWarningStore = inject(SaveGuardStore);
    readonly #templateCriteriaValidationService = inject(TemplateCriteriaValidationService);
    readonly #templateCriteriaValidationStateService = inject(TemplateCriteriaValidationStateService);
    readonly #syncValidationStateService = inject(SyncValidationStateService);
    readonly #invalidSaveWarningService = inject(AssetInvalidSaveWarningService);

    selectionCriteria: SelectionCriterionData[] = [];
    templateForm = new FormGroup(new TemplateCriteriaBuilderForm());

    readonly canDeactivate$ = new BehaviorSubject({ dirtied: false, valid: false });

    readonly saveGuardHandlerFn = this.#formSaveWarningStore.formUnsavedChangesWarningMisc;

    readonly allowPartialSave = true;

    private readonly canDeactivate = toSignal(this.canDeactivate$, { requireSync: true });

    public disableSave = computed<boolean>(() =>
        this.#templateCriteriaValidationStateService.shouldDisableSave(
            this.#selectedTemplateStore.template(),
            this.canDeactivate(),
            this.#templateCriteriaValidationStateService.invalidSections(),
            'recordSelection'
        )
    );

    constructor() {
        this.saveGuardHandlerFn({
            canDeactivate$: this.canDeactivate$,
            disableSave: this.disableSave,
        });
    }

    /**
     * @description Due to the complexity of the criteria shape and the timing of its initialization, we cannot
     * react to the form state reactively. So these two subscriptions which become live after the form is initialized
     * handle syncing the form dirtied/valid state semi-imperatively in a mildly reactive way.
     */
    handleFormSaveWarningAfterInit() {
        this.#formSaveWarningStore.formUnsavedChangesWarningAfterNgOnInit({
            form: this.templateForm,
            canDeactivate$: this.canDeactivate$,
            destroyRef$: this.#destroyRef$,
            disableSave: this.disableSave,
        });
    }

    loggedInUserId: Signal<number | undefined> = this.#globalLoggedInUserStore.selectedUserId;
    private selectedIamDivision$ = toObservable(this.#globalLoggedInUserStore.selectedIamDivision);
    private selectedDivisionConfig$ = toObservable(this.#globalLoggedInUserStore.selectedDivisionConfig);

    divisionConfiguredTable: Signal<DataMartTable | undefined> = this.#tableStore.table;
    genericCriteriaTables = this.#dataModelStore.genericCriteriaTables;
    modelTables = this.#dataModelStore.modelTables;

    divisionConfig: Signal<DivisionConfig | undefined> = this.#globalLoggedInUserStore.selectedDivisionConfig;
    divisionCriteriaSourceSettings: Signal<DivisionCriteriaSourceSettings | undefined> = computed(
        () => this.divisionConfig()?.config?.criteriaSourceSettings
    );

    salesHistoryCmtLevels = toSignal(
        this.#dataMartService.getSalesHistoryTargetLevels(
            this.divisionCriteriaSourceSettings()?.salesHistoryTable,
            this.divisionCriteriaSourceSettings()?.salesHistoryCmtColumn
        ),
        { initialValue: [] }
    );
    selfManufacturedOptions = toSignal(
        this.#dataMartService.getSelfManufacturedOptions(
            this.divisionCriteriaSourceSettings()?.salesHistoryTable,
            this.divisionCriteriaSourceSettings()?.salesHistorySelfMfgColumn
        ),
        { initialValue: [] }
    );
    sspOptions = toSignal(
        this.#dataMartService.getSspOptions(
            this.divisionCriteriaSourceSettings()?.salesHistoryTable,
            this.divisionCriteriaSourceSettings()?.salesHistorySspColumn
        ),
        { initialValue: [] }
    );
    privateLabelOptions = toSignal(
        this.#dataMartService.getPrivateLabelOptions(
            this.divisionCriteriaSourceSettings()?.salesHistoryTable,
            this.divisionCriteriaSourceSettings()?.salesHistoryProductLabelColumn
        ),
        { initialValue: [] }
    );
    salesHistoryChannels = toSignal(
        this.#dataMartService.getSalesHistoryChannels(
            this.divisionCriteriaSourceSettings()?.salesHistoryTable,
            this.divisionCriteriaSourceSettings()?.salesHistoryChannelsColumn
        ),
        { initialValue: [] }
    );
    salesHistoryMetricFields = toSignal(
        this.#dataMartService.getSalesHistoryMetrics(this.divisionCriteriaSourceSettings()?.salesHistoryTable),
        { initialValue: [] }
    );

    webActivityCmtLevels = toSignal(
        this.#dataMartService.getWebActivityTargetLevels(
            this.divisionCriteriaSourceSettings()?.webActivityTable,
            this.divisionCriteriaSourceSettings()?.webActivityCmtColumn
        ),
        { initialValue: [] }
    );
    webActivityPeriodFields = toSignal(
        this.#dataMartService.getWebActivityPeriods(this.divisionCriteriaSourceSettings()?.webActivityTable),
        { initialValue: [] }
    );

    datamartRequiredField = computed(
        () => this.#globalLoggedInUserStore.selectedDivisionConfig()?.config?.mapping?.dataMartRequiredField
    );

    allowedSelectionCriteria = computed(() => this.divisionConfig()?.config?.mapping?.allowedCriteria ?? []);

    ngOnInit(): void {
        this.selectedIamDivision$.pipe(filterNil()).subscribe(() => {
            this.#dataModelStore.getDataModel();
        });
        this.selectedDivisionConfig$.pipe(filterNil()).subscribe(selectedDivisionConfig => {
            const tableName = selectedDivisionConfig?.config?.mapping?.dataMartTable;
            if (tableName) {
                this.#tableStore.getTable({ tableName: tableName });
            }
        });
        combineLatest([
            this.#tableStore.table$.pipe(filterNil()),
            this.#selectedTemplateStore.template$.pipe(filterNil()),
        ])
            .pipe(take(1))
            .subscribe(([_, template]) => {
                const criteria = template.config.selectionCriteria ?? [];
                criteria.forEach(criterion => {
                    this.templateForm.controls.selectionCriteria.push(
                        new FormGroup(new SelectionCriterionForm(criterion.type, criterion))
                    );
                });
                this.templateForm.patchValue(new FormGroup(new TemplateCriteriaBuilderForm(template)).getRawValue());
                if (!this.templateForm.controls.selectionCriteria.value.length) {
                    this.setRequiredCriterionFormGroup();
                }

                this.handleFormSaveWarningAfterInit();
                this.subscribeToValidationState(
                    this.templateForm,
                    'recordSelection',
                    this.#templateCriteriaValidationService,
                    this.#templateCriteriaValidationStateService
                );
                this.templateForm.markAllAsTouched(); // this makes the form indicate invalid fields on load
            });

        const templateId: string = this.#route.snapshot.params['id'];
        if (templateId && templateId !== 'new' && this.#selectedTemplateStore.template()?.id !== +templateId) {
            this.#selectedTemplateStore.getTemplate(+templateId);
        }
        this.#selectedTemplateStore.message$.pipe(filterNil()).subscribe(message => {
            this.#assetStateMessageHandlingService.handleMessage(message);
            this.#selectedTemplateStore.resetMessage();
        });
    }

    ngAfterViewChecked(): void {
        this.#changeDetectorRef.detectChanges();
    }

    private setRequiredCriterionFormGroup() {
        const type = this.divisionConfiguredTable()?.fields.at(0)?.selectCriteria;
        if (type === 'Model Criteria') {
            const requiredCriterionForm = new FormGroup(new SelectionCriterionForm('model'));
            this.templateForm.controls.selectionCriteria.push(requiredCriterionForm);
        } else if (type === 'Generic Criteria') {
            const requiredCriterionForm = new FormGroup(new SelectionCriterionForm('generic'));
            this.templateForm.controls.selectionCriteria.push(requiredCriterionForm);
        }
        this.templateForm.controls.selectionCriteria.markAsDirty();
    }

    onAddCriteria(criteriaType: SelectionCriterionType) {
        const criterionFormGroup = new FormGroup<SelectionCriterionForm>(new SelectionCriterionForm(criteriaType));
        if (criteriaType === 'sales history') {
            const availableChannels = this.salesHistoryChannels().map(channel => channel.value);
            (
                criterionFormGroup.controls.data as FormGroup<SalesHistorySelectionCriterionDataForm>
            ).controls.channel.setValue(availableChannels);
        }
        this.templateForm.controls.selectionCriteria.push(criterionFormGroup);
        this.templateForm.controls.selectionCriteria.markAllAsDirty();
        this.templateForm.markAllAsDirty();
        this.templateForm.controls.selectionCriteria.updateValueAndValidity();
        this.templateForm.controls.selectionCriteria.markAllAsTouched();
    }

    subscribeToValidationState(
        form: FormGroup<TemplateCriteriaBuilderForm>,
        sectionName: TemplateCriteriaSectionName,
        validationService: ValidationService<TemplateCriteriaSectionName, TemplateCriteria, TemplateCriteriaFormMap>,
        validationStateService: ValidationStateService<TemplateCriteriaSectionName>
    ) {
        return this.#syncValidationStateService.subToValidationState(
            form,
            sectionName,
            validationService,
            validationStateService
        );
    }

    async onSave() {
        const selectionCriteria: SelectionCriterion[] = [];
        this.templateForm.controls.selectionCriteria?.controls.forEach(criterionForm => {
            let fields = [] as DataMartField[];
            if (criterionForm.controls.type.value === 'generic') {
                const data = criterionForm.controls.data as FormGroup<GenericSelectionCriterionForm>;
                const table = this.genericCriteriaTables()?.find(tbl => tbl.name === data.controls.tableName.value);
                fields = table?.fields ?? [];
            } else if (criterionForm.controls.type.value === 'model') {
                const data = criterionForm.controls.data as FormGroup<GenericSelectionCriterionForm>;
                const table = this.modelTables()?.find(tbl => tbl.name === data.controls.tableName.value);
                fields = table?.fields ?? [];
            }
            selectionCriteria.push(convertSelectionCriterionForm(criterionForm, fields ?? []));
        });
        const existingTemplate = this.#selectedTemplateStore.template();
        const config: TemplateCriteriaConfig = {
            ...existingTemplate?.config,
            groupingLogic: {
                groupingLogic: this.templateForm.controls.groupingLogic.controls.groupingLogic.value,
                type: this.templateForm.controls.groupingLogic.controls.type.value,
            },
            selectionCriteria: selectionCriteria,
        };
        const template = {
            ...existingTemplate,
            id: existingTemplate?.id,
            updatedBy: this.loggedInUserId(),
            valid: this.#templateCriteriaValidationStateService.invalidSections().length === 0,
        };

        if (!(await this.#invalidSaveWarningService.confirmSave('template criteria', template))) {
            return noopFormSaveMutationResult();
        }
        return this.#selectedTemplateStore.saveTemplateAndConfig({ template: template, config: config });
    }

    onDeleteCriterion(index: number) {
        this.templateForm.controls.selectionCriteria.removeAt(index);
        // Form arrays being programatically updated do not mark dirty automatically
        this.templateForm.controls.selectionCriteria.markAllAsDirty();
        this.templateForm.markAllAsDirty();
        this.templateForm.controls.selectionCriteria.updateValueAndValidity();
        this.templateForm.markAllAsDirty();
    }
}
